import { PencilIcon, PlusIcon, SaveIcon, TrashIcon, ArrowLeftIcon } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import SchoolList from '../../components/shared/SchoolList';
import { apiConnecter } from '../../services/apiConnector';
import { useSelector } from "react-redux"
import API_ENDPOINTS from '../../constant/EndPoints';
import ThemePortalConfig from '../../components/shared/ThemePortalConfig';


// --- MOCK DATA & API CALLS (from section 1) ---
const generateUniqueId = () => `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;


const api = {
    fetchSchools: async (userInfo) => {
        const response = await apiConnecter("GET", API_ENDPOINTS.UNITE_US_GCU_GET_SCHOOL_DATA_END_POINT, "", { authorization: `Bearer ${userInfo.token}` })
        return response.data.result;
    },
    createSchool: async (userInfo, data) => {
        const response = await apiConnecter("POST", API_ENDPOINTS.UNITE_US_GCU_SET_SCHOOL_DATA_END_POINT, data, { authorization: `Bearer ${userInfo.token}` })
    },
    updateSchool: async (userInfo, schoolId, data) => {
        const response = await apiConnecter("PUT", `${API_ENDPOINTS.UNITE_US_GCU_UPDATE_SCHOOL_DATA_END_POINT}/${schoolId}`, data, { authorization: `Bearer ${userInfo.token}` })
    },
    deleteSchool: async (userInfo, schoolId) => {
        const response = await apiConnecter("DELETE", `${API_ENDPOINTS.UNITE_US_GCU_DELETE_SCHOOL_DATA_END_POINT}/${schoolId}`, null, { authorization: `Bearer ${userInfo.token}` })
    }
};

// --- COMMON UI COMPONENTS ---
const IconButton = ({ icon: Icon, label, onClick, className = '', disabled = false }) => (
    <button
        onClick={onClick}
        className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        aria-label={label}
        disabled={disabled}
    >
        <Icon className="w-5 h-5" />
        <span className='hidden sm:inline'>{label}</span>
    </button>
);

const INPUT_CLASSES = "w-full p-2 rounded-lg bg-[#262626] border border-gray-700 text-white focus:ring-[#7436D2] focus:border-[#7436D2]";
const SECTION_TITLE_CLASSES = "text-xl font-semibold text-white mb-4";
const CARD_CLASSES = "bg-[#1f1f1f] rounded-2xl border border-gray-800 p-6 shadow-xl";


// --- THEME PORTAL CONFIG COMPONENT (CREATE/UPDATE) ---
// This component is the one you provided, slightly modified for dynamic ID and SAVE function

// --- MAIN CONTAINER COMPONENT (CRUD LOGIC) ---
const SchoolConfigurationPage = () => {
    const [view, setView] = useState('list'); // 'list' or 'form'
    const userInfo = useSelector((state) => state.userInfo);
    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState(null); // School object for update/create
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    // --- API & State Management ---

    // READ: Fetch all schools
    const fetchSchools = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await api.fetchSchools(userInfo);
            setSchools(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchSchools();
    }, []);

    // CREATE/UPDATE: Handle save from form
    const handleSave = async (config) => {
        setIsSaving(true);
        setError(null);
        try {
            if (config._id) {
                // Update existing school
                await api.updateSchool(userInfo, config._id, config);
                alert(`Configuration for ${config.name} updated successfully!`);
            } else {
                // Create new school
                await api.createSchool(userInfo, config);
                alert(`New school configuration for ${config.name} created successfully!`);
            }
            // Refetch data and return to list view
            await fetchSchools();
            setView('list');
            setSelectedSchool(null);

        } catch (err) {
            setError(err.message);
            alert(`Error saving configuration: ${err.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    // DELETE: Handle school deletion
    const handleDelete = async (_id, name) => {
        if (!window.confirm(`Are you sure you want to delete the configuration for ${name}? This action is irreversible.`)) {
            return;
        }

        setIsLoading(true); // Set loading while deleting
        setError(null);
        try {
            await api.deleteSchool(userInfo, _id);
            alert(`Configuration for ${name} deleted successfully!`);
            await fetchSchools(); // Refetch list after deletion
        } catch (err) {
            setError(err.message);
            alert(`Error deleting configuration: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // --- View State Handlers ---

    // Handler for "Add New School" button
    const handleAdd = () => {
        setSelectedSchool(null); // Pass null to indicate a new creation
        setView('form');
    };

    // Handler for "Update" button
    const handleEdit = (school) => {
        setSelectedSchool(school);
        setView('form');
    };

    // Handler for "Back" button or successful save
    const handleCancel = () => {
        setSelectedSchool(null);
        setView('list');
    };

    return (
        <div className="min-h-screen bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] p-4 sm:p-8 w-full">
            <div className="max-w-7xl mx-auto">
                {view === 'list' && (
                    <SchoolList
                        schools={schools}
                        onAdd={handleAdd}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        isLoading={isLoading}
                        error={error}
                    />
                )}
                {view === 'form' && (
                    <ThemePortalConfig
                        initialConfig={selectedSchool}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        isSaving={isSaving}
                    />
                )}
            </div>
        </div>
    );
};

export default SchoolConfigurationPage;